import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { parseCarbonFactors,
  entriesFromMonthly,
  entriesFromRecords,
  CompanyIdSchema,
  DISPOSAL_INFO,
  groupColor,
  type CustomerYearData,
  type DisposalMethod,
  type CarbonRouting,
  type CompanyGroupInfo,
} from '@igf-minar/shared'
import { api, ApiError } from '../lib/api'
import { pushRecentCompany } from '../lib/recent'
import { usePreferences } from '../lib/preferences'
import { useCurrentUser, isAdminRole } from '../lib/auth'
import { OverviewTab, type OverviewData } from '../components/dashboard/OverviewTab'
import { StadsetningarTab } from '../components/dashboard/StadsetningarTab'
import { FlokkagreiningTab } from '../components/dashboard/FlokkagreiningTab'
import { KostnadurTab } from '../components/dashboard/KostnadurTab'
import { KolefnissporTab } from '../components/dashboard/KolefnissporTab'
import { LosanirTab } from '../components/dashboard/LosanirTab'
import { SamanburdurTab } from '../components/dashboard/SamanburdurTab'
import { GraentBokhaldTab } from '../components/dashboard/GraentBokhaldTab'
import { SamstaedaTab } from '../components/dashboard/SamstaedaTab'
import { messagesFor, useMessages } from '@/i18n/messages'
import { companyPageMessages } from '@/i18n/text/companyPage'
import { disposalName, groupName, locationName } from '@/i18n/domain'
import { monthName } from '@/lib/dates'

const searchYear = (value: unknown): number => {
  const year = Number(value)
  return Number.isInteger(year) && year >= 2000 && year <= 2100 ? year : new Date().getFullYear()
}

const SearchSchema = (search: Record<string, unknown>): { year: number; view?: 'samstaeda' } => ({
  year: searchYear(search.year),
  ...(search.view === 'samstaeda' ? { view: 'samstaeda' as const } : {}),
})

type CompanyDataResponse = Awaited<ReturnType<typeof api.companyData>> & { group?: CompanyGroupInfo }

const fetchCompanyData = (id: string, year: number, groupView: boolean): Promise<CompanyDataResponse> =>
  groupView ? api.companyGroupData(id, year) : api.companyData(id, year)

export const Route = createFileRoute('/companies/$id')({
  validateSearch: SearchSchema,
  parseParams: (params) => {
    const parsed = CompanyIdSchema.safeParse(params.id)
    if (!parsed.success) throw notFound()
    return { id: parsed.data }
  },
  component: CompanyDataPage,
})

const AVAILABLE_YEARS = [2024, 2025, 2026]
const ACCENT = '#00A04B'

type TabId =
  | 'samstaeda'
  | 'yfirlit'
  | 'stadsetningar'
  | 'losanir'
  | 'kostnadur'
  | 'flokkar'
  | 'kolefnisspor'
  | 'samanburdur'
  | 'graent-bokhald'

type TabLabelKey =
  | 'tabGroupMembers'
  | 'tabOverview'
  | 'tabLocations'
  | 'tabCollections'
  | 'tabCost'
  | 'tabCategories'
  | 'tabCarbon'
  | 'tabComparison'
  | 'tabDownload'

const TABS: { id: TabId; label: TabLabelKey }[] = [
  { id: 'yfirlit', label: 'tabOverview' },
  { id: 'stadsetningar', label: 'tabLocations' },
  { id: 'losanir', label: 'tabCollections' },
  { id: 'kostnadur', label: 'tabCost' },
  { id: 'flokkar', label: 'tabCategories' },
  { id: 'kolefnisspor', label: 'tabCarbon' },
  { id: 'samanburdur', label: 'tabComparison' },
  { id: 'graent-bokhald', label: 'tabDownload' },
]

function CompanyDataPage() {
  const m = useMessages(companyPageMessages)
  const { id } = Route.useParams()
  const { year, view } = Route.useSearch()
  const navigate = Route.useNavigate()
  const setYear = (y: number) => { void navigate({ search: (prev) => ({ ...prev, year: y }) }) }
  const setView = (v: 'samstaeda' | undefined) => {
    setTab(v ? 'samstaeda' : 'yfirlit')
    void navigate({ search: (prev) => ({ year: prev.year, ...(v ? { view: v } : {}) }) })
  }
  const [tab, setTab] = useState<TabId>(view === 'samstaeda' ? 'samstaeda' : 'yfirlit')
  const { isFavorite, toggleFavorite } = usePreferences()
  const { data: me } = useCurrentUser()
  const canFavorite = isAdminRole(me)

  // Company metadata (name, kennitala) is fetched independently of the yearly
  // data so the header stays correct even when a given year has no records.
  const { data: companyMeta, error: metaError, isLoading: metaLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: () => api.company(id),
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => count < 1 && !(err instanceof ApiError && err.status === 404),
  })

  const groupRef = companyMeta?.group ?? null
  const groupView = view === 'samstaeda' && !!groupRef
  const activeTab: TabId = tab === 'samstaeda' && !groupView ? 'yfirlit' : tab
  const tabs = groupView ? [{ id: 'samstaeda' as const, label: 'tabGroupMembers' as const }, ...TABS] : TABS
  const scope = groupView ? 'group' : 'own'

  const { data, isLoading, error } = useQuery({
    queryKey: ['companyData', id, year, scope],
    queryFn: () => fetchCompanyData(id, year, groupView),
    enabled: !!companyMeta || view !== 'samstaeda',
    retry: (count, err) => count < 1 && !(err instanceof ApiError && err.status === 404),
  })

  const company = data?.company ?? companyMeta?.company
  const pageLoading = isLoading || (view === 'samstaeda' && metaLoading)
  const pageError = error ?? (view === 'samstaeda' && !companyMeta ? metaError : null)
  const reportName = groupView && groupRef ? groupRef.name : data?.company.name ?? company?.name ?? id

  useEffect(() => {
    if (company) {
      pushRecentCompany({ id, name: company.name, shortName: company.shortName })
    }
  }, [company, id])

  const { data: prevData, isLoading: prevLoading } = useQuery({
    queryKey: ['companyData', id, year - 1, scope],
    queryFn: () => fetchCompanyData(id, year - 1, groupView),
    enabled: (activeTab === 'samanburdur' || activeTab === 'graent-bokhald') && (!!companyMeta || view !== 'samstaeda'),
    retry: (count, err) => count < 1 && !(err instanceof ApiError && err.status === 404),
  })

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <Link to="/" className="text-sm text-gray-500 hover:text-emerald-700">
            {m.backToOverview}
          </Link>
          <div className="mt-2 flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{groupView && groupRef ? groupRef.name : company?.name ?? id}</h1>
            {canFavorite && (
              <button
                onClick={() => toggleFavorite(id)}
                title={isFavorite(id) ? m.removeFavorite : m.addFavorite}
                className={`text-2xl leading-none transition ${
                  isFavorite(id) ? 'text-amber-400 hover:text-amber-500' : 'text-gray-300 hover:text-amber-400'
                }`}
              >
                {isFavorite(id) ? '★' : '☆'}
              </button>
            )}
          </div>
          {company?.kennitala && (
            <p className="text-sm text-gray-500 font-mono mt-1">
              {groupView ? m.parentCompany(company.name, company.kennitala) : m.kennitalaShort(company.kennitala)}
            </p>
          )}
          {groupRef && (
            <div className="mt-3 inline-flex rounded-full border border-[var(--border)] bg-[var(--surface)] p-0.5 text-sm">
              <button
                onClick={() => setView(undefined)}
                className={`px-3 py-1 rounded-full transition-colors ${groupView ? 'text-[var(--muted)] hover:text-[var(--fg)]' : 'text-white'}`}
                style={groupView ? undefined : { backgroundColor: ACCENT }}
              >
                {company?.shortName ?? company?.name ?? id}
              </button>
              <button
                onClick={() => setView('samstaeda')}
                className={`px-3 py-1 rounded-full transition-colors ${groupView ? 'text-white' : 'text-[var(--muted)] hover:text-[var(--fg)]'}`}
                style={groupView ? { backgroundColor: ACCENT } : undefined}
              >
                {m.groupToggle(groupRef.memberCount)}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{m.yearLabel}</span>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none bg-white"
          >
            {AVAILABLE_YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1.5 mb-6">
        {tabs.map((t) => {
          const active = activeTab === t.id
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                active
                  ? 'text-white border-transparent'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              style={active ? { backgroundColor: ACCENT } : undefined}
            >
              {m[t.label]}
            </button>
          )
        })}
      </div>

      {pageLoading && <LoadingState group={view === 'samstaeda'} />}
      {pageError && <ErrorState error={pageError} year={year} />}
      {data?.data && (
        <>
          {activeTab === 'samstaeda' && data.group && (
            <SamstaedaTab group={data.group} data={data.data} year={year} accent={ACCENT} />
          )}
          {activeTab === 'yfirlit' && <YfirlitTab data={data.data} routing={company?.carbonRouting} />}
          {activeTab === 'losanir' && (
            <LosanirTab rawRecords={data.data.rawRecords} year={year} accent={ACCENT} />
          )}
          {activeTab === 'kostnadur' && (
            <KostnadurTab data={data.data} year={year} accent={ACCENT} />
          )}
          {activeTab === 'flokkar' && (
            <FlokkagreiningTab data={data.data} rawRecords={data.data.rawRecords} accent={ACCENT} />
          )}
          {activeTab === 'kolefnisspor' && (
            <KolefnissporTab
              year={year}
              accent={ACCENT}
              records={data.data.rawRecords}
              monthly={data.data.monthly}
              routing={company?.carbonRouting}
              methodology={data.carbonMethodology}
              factors={parseCarbonFactors(data.carbonFactors)}
            />
          )}
          {/* key={id-year}: these tabs hold filter/selection state derived from
              the loaded year (selected months, location filters), so they must
              remount — not just re-render — when the company or year changes. */}
          {activeTab === 'stadsetningar' && (
            <StadsetningarTab
              key={`${id}-${year}-${scope}`}
              companyId={id}
              group={groupView}
              year={year}
              locations={data.data.locations}
              rawRecords={data.data.rawRecords}
              accent={ACCENT}
            />
          )}
          {activeTab === 'samanburdur' && (
            <SamanburdurTab
              key={`${id}-${year}-${scope}`}
              data={data.data}
              prevData={prevData?.data}
              year={year}
              accent={ACCENT}
              loadingPrev={prevLoading}
            />
          )}
          {activeTab === 'graent-bokhald' && (
            <GraentBokhaldTab
              key={`${id}-${year}-${scope}`}
              rawRecords={data.data.rawRecords ?? []}
              prevYearRecords={prevData?.data?.rawRecords}
              year={year}
              companyName={reportName}
              companySlug={groupView ? `${id}-${m.groupSlug}` : id}
              accent={ACCENT}
              onExport={(report, format) => api.activity.export(id, { report, format, year, group: groupView })}
            />
          )}
        </>
      )}
    </div>
  )
}

function LoadingState({ group = false }: { group?: boolean }) {
  const m = useMessages(companyPageMessages)
  return (
    <div className="flex items-center gap-3 text-gray-500 py-12">
      <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      {group ? m.loadingGroup : m.loading}
    </div>
  )
}

function ErrorState({ error, year }: { error: unknown; year: number }) {
  const m = useMessages(companyPageMessages)
  const msg = error instanceof Error ? error.message : String(error)
  const isNotFound = error instanceof ApiError && error.status === 404
  const isUnavailable = error instanceof ApiError && error.status === 503
  return (
    <div
      className={`rounded-xl p-6 border ${
        isNotFound
          ? 'bg-amber-50 border-amber-200 text-amber-900'
          : isUnavailable
            ? 'bg-sky-50 border-sky-200 text-sky-900'
            : 'bg-red-50 border-red-200 text-red-900'
      }`}
    >
      <h2 className="font-semibold">{isNotFound ? m.noDataTitle : isUnavailable ? m.unavailableTitle : m.errorTitle}</h2>
      <p className="mt-2 text-sm">{msg}</p>
      {isNotFound && (
        <p className="mt-3 text-sm">
          {m.tryAnotherYearBefore}{' '}<strong>{year}</strong>{m.tryAnotherYearAfter}
        </p>
      )}
    </div>
  )
}

/** Adapter: CustomerYearData → OverviewData (mirrors Lausnir YfirlitTab). */
function buildOverviewData(
  data: CustomerYearData,
  routing?: CarbonRouting | null,
  recyclingTarget = 85
): OverviewData {
  const { summary, monthly, locations, rawRecords } = data

  const groupTotals = new Map<string, number>()
  for (const m of monthly) {
    for (const g of m.groups) {
      if (g.kg > 0) groupTotals.set(g.group, (groupTotals.get(g.group) || 0) + g.kg)
    }
  }
  const categories = [...groupTotals.entries()]
    .map(([key, kg]) => ({ key, name: groupName(key), color: groupColor(key), kg }))
    .sort((a, b) => b.kg - a.kg)

  const disposalTotals: Record<DisposalMethod, number> = {
    endurvinnsla: 0, orkunyting: 0, brennsla: 0, urdun: 0,
  }
  const entries = rawRecords && rawRecords.length > 0
    ? entriesFromRecords(rawRecords, routing)
    : entriesFromMonthly(monthly, routing)
  for (const e of entries) {
    disposalTotals[e.disposal] += e.kg
  }
  const disposal = Object.entries(DISPOSAL_INFO)
    .map(([key, info]) => ({
      key,
      name: disposalName(key as DisposalMethod),
      value: disposalTotals[key as DisposalMethod],
      color: info.color,
    }))
    .filter((d) => d.value > 0)

  const monthlyData = monthly.map((m) => ({
    monthLabel: monthName(m.month - 1),
    totalKg: m.totalKg,
    sortingRate: m.sortingRate,
  }))

  const properties = locations.map((loc) => ({
    id: loc.location,
    name: locationName(loc.location),
    totalKg: loc.totalKg,
    recyclableKg: loc.recyclableKg,
    sortingRate: loc.sortingRate,
  }))

  return {
    totalKg: summary.totalKg,
    recyclableKg: summary.recyclableKg,
    sortingRate: summary.sortingRate,
    collectionCount: summary.collectionCount,
    propertyCount: summary.locationCount,
    monthly: monthlyData,
    categories,
    disposal,
    properties,
    recyclingTarget,
    accent: ACCENT,
    propertiesLabel: messagesFor(companyPageMessages).tabLocations,
  }
}

function YfirlitTab({ data, routing }: { data: CustomerYearData; routing?: CarbonRouting | null }) {
  const overview = useMemo(() => buildOverviewData(data, routing), [data, routing])
  return <OverviewTab data={overview} />
}
